import type {AgentSubType, CollectionSubType, JSONValue} from 'types';
import type {WorkMetadata} from '../metadata/types';

export const HighlightPreTag = 'HIGHLIGHT_START';
export const HighlightPostTag = 'HIGHLIGHT_END';

export type AgentSearchProps = {
  query: string;
  agentType: AgentSubType;
  page: number;
  size: number;
  abortSignal?: AbortSignal;
};

export type SeriesSearchProps = {
  query: string;
  seriesType: CollectionSubType;
  page: number;
  size: number;
  abortSignal?: AbortSignal;
};

export type AgentsCountSearchProps = {
  query: string;
  abortSignal?: AbortSignal;
};

export type SeriesCountSearchProps = {
  query: string;
  abortSignal?: AbortSignal;
};

export type AgentsCountResult = Partial<Record<AgentSubType, number>>;

export type SeriesCountResult = Partial<Record<CollectionSubType, number>>;

export type WorkSearchProps = {
  query: string;
  page: number;
  size: number;
  includeInternationalWorks?: boolean;
  abortSignal?: AbortSignal;
};

export type PrizeSearchProps = {
  query: string;
  page: number;
  size: number;
  abortSignal?: AbortSignal;
};

export type AgentWorksSearchProps = {
  agentId: string;
  page: number;
  size: number;
  abortSignal?: AbortSignal;
};

/**
 * Fully-rendered elastic lambda payload plus an optional abort signal.
 * Each `map*SearchPropsToSearchProps` mapper is responsible for substituting
 * placeholders in its query template (page/size, query string, type filter,
 * highlight, sort, etc.) so the elastic body needs no further composition
 * before it is sent.
 */
export type SearchProps = ElasticCloudAuthLambda & {
  abortSignal?: AbortSignal;
};

// Search indexes
export type SearchType = 'catalog' | 'agent' | 'collection';

export type ElasticCloudAuthLambda = {
  target: '_search' | '_msearch';
  index: string;
  elasticBody: JSONValue;
};

export type Highlighter = {
  query: string;
  fields: string[];
};

export type SearchResult<T> = {
  hits: T[];
  highlights: string[];
  total: number;
};

export type SearchResultDto<Source> = {
  _id: string;
  took: number;
  timed_out: boolean;
  _shards: ShardsDto;
  hits: HitsDto<Source>;
};

export type ShardsDto = {
  total: number;
  successful: number;
  skipped: number;
  failed: number;
};

export type HitsDto<Source> = {
  total: {
    value: number;
    relation: string;
  };
  max_score: number;
  hits: HitDto<Source>[];
};

// Elastic Multi Search Result
export type SearchCountResultDto = {
  took: number;
  responses: Omit<SearchResultDto<never>, 'hits'> &
    {
      hits: Pick<HitsDto<never>, 'total'>;
    }[];
};

export type HighlightDto = Record<string, string[]>;

export type HitDto<Source> = {
  _index: Index;
  _id: string;
  _score: number;
  _source: Source;
  highlight?: HighlightDto;
};

export type Index =
  | 'dev-agent-cms'
  | 'dev-catalog-cms'
  | 'dev-search-series'
  | 'prod-search-agent'
  | 'prod-search-catalog'
  | 'prod-search-series';

/**
 * WorkHit is currently just an alias for WorkMetadata, but WorkHit (search response) might
 * be extended in the future. Also since WorkHit is sourced from elastic and not API, it should
 * not be used in any functional manner (e.g. linking), only presentations.
 */
export type WorkHit = WorkMetadata & {
  id: string;
};
